import React, { useEffect, useMemo, useRef, useState, type JSX } from 'react';
import type { CommandDdbItem } from '../../../../types/Commands';

import { useDeviceListLiveData } from '../../DeviceListView/utils/useDeviceListLiveData';
import { PortaledLayer } from '../../shared/PortaledLayer';
import {
  buildRaceValidationReportCsv,
  RaceValidationReportTable,
} from '../../shared/RaceValidationReportTable';
import type { EventDeviceRow } from '../types';
import {
  getDeviceValidationBreakdown,
  getDeviceValidationBreakdownWhenRaceConfigurationMissing,
  isRaceConfigurationReadyForDeviceVerification,
  type RaceConfigurationRequirements,
} from '../../../helpers/deviceValidation';
import { buildRaceValidationReportRows } from '../../../helpers/raceValidationReportRows';
import { groupDevicesForValidationReport } from '../../../helpers/devicePairing';

type Props = {
  open: boolean;
  onClose: () => void;
  eventId: string;
  raceId: string;
  raceName?: string;
  devices: EventDeviceRow[];
  statusReloadKey: string | number;
};

export default function RaceValidationReportDialog(props: Props): JSX.Element | null {
  const { open, onClose, eventId, raceId, raceName, devices, statusReloadKey } = props;

  const activeRequestIdRef = useRef<string | null>(null);
  const [, setRequestLatestCommandMap] = useState<Record<string, CommandDdbItem>>({});

  const deviceIds = useMemo(() => {
    const refs = devices.map((d) => ({
      deviceId: String(d.deviceId || '').trim(),
      assignedAthleteId: d.assignedAthleteId,
    })).filter((d) => d.deviceId);
    const { groups, standalone } = groupDevicesForValidationReport(refs);
    return [
      ...groups.map((g) => g.rearDeviceId),
      // Paired front IDs — needed so statusMap has fresh telemetry to check
      // front firmware against, not just the rear's own status.
      ...groups.map((g) => g.frontDeviceId).filter(Boolean),
      ...standalone.map((d) => d.deviceId),
    ].filter(Boolean);
  }, [devices]);

  const { statusMap } = useDeviceListLiveData({
    activeRequestIdRef,
    setRequestLatestCommandMap,
    deviceIds,
    operatorScopedEventId: eventId,
    enableInitialStatusQuery: true,
    enableInitialCommandQuery: false,
    statusReloadKey,
  });

  const [raceCfg, setRaceCfg] = useState<RaceConfigurationRequirements | null>(null);
  const [cfgLoading, setCfgLoading] = useState(false);
  const [cfgError, setCfgError] = useState<string | null>(null);

  const [lifecycleById, setLifecycleById] = useState<Record<string, Record<string, unknown>>>({});

  useEffect(() => {
    if (!open || !eventId.trim() || !raceId.trim()) {
      setRaceCfg(null);
      setCfgError(null);
      setCfgLoading(false);
      return;
    }
    const api = (
      window.eventsAPI as { getRaceConfiguration?: (p: { eventId: string; raceId: string }) => Promise<unknown> }
    )?.getRaceConfiguration;
    if (!api) {
      setRaceCfg(null);
      setCfgError('Race configuration API unavailable');
      return;
    }
    let cancelled = false;
    setCfgLoading(true);
    setCfgError(null);
    void api({ eventId, raceId })
      .then((res: unknown) => {
        if (cancelled) return;
        const r = res as { ok?: boolean; data?: Record<string, unknown> } | null | undefined;
        if (!r?.ok) {
          setRaceCfg(null);
          setCfgError('Failed to load race configuration');
          return;
        }
        const data = (r?.data ?? {}) as Record<string, unknown>;
        const raw = (data.configuration ?? {}) as Record<string, unknown>;
        const cfg: RaceConfigurationRequirements = {
          fw_version: typeof raw.fw_version === 'string' ? raw.fw_version.trim() : '',
          fw_version_front: typeof raw.fw_version_front === 'string' ? raw.fw_version_front.trim() : '',
          min_battery: Number.isFinite(Number(raw.min_battery)) ? Number(raw.min_battery) : undefined,
          max_debug_size: Number.isFinite(Number(raw.max_debug_size)) ? Number(raw.max_debug_size) : undefined,
          max_race_size: Number.isFinite(Number(raw.max_race_size)) ? Number(raw.max_race_size) : undefined,
        };
        setRaceCfg(cfg);
      })
      .catch(() => {
        if (!cancelled) {
          setRaceCfg(null);
          setCfgError('Failed to load race configuration');
        }
      })
      .finally(() => {
        if (!cancelled) setCfgLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, eventId, raceId]);

  useEffect(() => {
    const api = (
      window.eventsAPI as { getDeviceLifecycle?: (args: { deviceIds: string[] }) => Promise<unknown> }
    )?.getDeviceLifecycle;
    if (!open || !api || deviceIds.length === 0) {
      setLifecycleById({});
      return;
    }
    let cancelled = false;
    void api({ deviceIds })
      .then((res: unknown) => {
        if (cancelled) return;
        const r = res as { ok?: boolean; data?: { devices?: unknown[] } } | null | undefined;
        if (!r?.ok) {
          setLifecycleById({});
          return;
        }
        const rows = Array.isArray(r.data?.devices) ? r.data.devices : [];
        const map: Record<string, Record<string, unknown>> = {};
        for (const raw of rows) {
          if (!raw || typeof raw !== 'object') continue;
          const row = raw as Record<string, unknown>;
          const deviceId = String(row.deviceId ?? '').trim();
          if (deviceId) map[deviceId] = row;
        }
        setLifecycleById(map);
      })
      .catch(() => {
        if (!cancelled) setLifecycleById({});
      });
    return () => {
      cancelled = true;
    };
  }, [open, deviceIds]);

  const raceConfigReadyForVerification = isRaceConfigurationReadyForDeviceVerification(
    raceCfg,
    !cfgError,
  );

  const rows = useMemo(() => {
    const refs = devices.map((d) => ({
      deviceId: String(d.deviceId || '').trim(),
      assignedAthleteId: d.assignedAthleteId,
    })).filter((d) => d.deviceId);

    return buildRaceValidationReportRows({
      devices: refs,
      getBreakdown: (deviceId, frontDeviceId) => {
        const status = statusMap[deviceId];
        const life = lifecycleById[deviceId];
        const frontStatus = frontDeviceId ? statusMap[frontDeviceId] : undefined;
        return raceConfigReadyForVerification
          ? getDeviceValidationBreakdown(raceCfg, status, life, undefined, frontStatus)
          : getDeviceValidationBreakdownWhenRaceConfigurationMissing();
      },
    });
  }, [devices, statusMap, lifecycleById, raceCfg, raceConfigReadyForVerification]);

  const csvText = useMemo(
    () => buildRaceValidationReportCsv({ eventId, raceId, rows }),
    [rows, eventId, raceId],
  );

  async function copyCsv(): Promise<void> {
    try {
      if (!navigator?.clipboard?.writeText) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(csvText);
      window.dispatchEvent(new CustomEvent('toast-info', { detail: 'Validation report CSV copied' }));
    } catch {
      window.dispatchEvent(new CustomEvent('toast-error', { detail: 'Unable to copy CSV' }));
    }
  }

  function downloadCsv(): void {
    const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const safeEvent = String(eventId).replace(/[^a-zA-Z0-9_-]+/g, '_');
    const safeRace = String(raceId).replace(/[^a-zA-Z0-9_-]+/g, '_');
    const link = document.createElement('a');
    link.href = url;
    link.download = `race-validation-${safeEvent}-${safeRace}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const title = raceName?.trim() || raceId;

  return (
    <PortaledLayer>
      <div
        data-race-stacked-overlay=""
        className="fixed inset-0 z-[5100] flex items-center justify-center bg-black/40 p-3 dark:bg-black/70 sm:p-6"
        role="dialog"
        aria-modal="true"
        aria-labelledby="race-validation-report-title"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div
          className="flex max-h-[min(95vh,1100px)] w-full max-w-[min(1600px,100vw-1.5rem)] flex-col overflow-hidden rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-800">
            <div className="min-w-0">
              <h2 id="race-validation-report-title" className="text-sm font-semibold text-gray-900 dark:text-slate-100">
                Validation report
              </h2>
              <p className="mt-0.5 break-words text-xs text-gray-500 dark:text-slate-400">{title}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => void copyCsv()}
                disabled={rows.length === 0}
                className="rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Copy CSV
              </button>
              <button
                type="button"
                onClick={downloadCsv}
                disabled={rows.length === 0}
                className="rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Download CSV
              </button>
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800"
              >
                Close
              </button>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-auto bg-white px-4 py-3 dark:bg-slate-900">
            {cfgLoading ? (
              <p className="text-sm text-gray-600 dark:text-slate-400">Loading race configuration…</p>
            ) : cfgError ? (
              <p className="text-sm text-red-800 dark:text-red-300">{cfgError}</p>
            ) : deviceIds.length === 0 ? (
              <p className="text-sm text-gray-600 dark:text-slate-400">No devices assigned to this race.</p>
            ) : (
              <RaceValidationReportTable rows={rows} eventId={eventId} />
            )}
          </div>
        </div>
      </div>
    </PortaledLayer>
  );
}
